import React, { useCallback, useState } from "react";
import { FiCheck, FiCopy } from "react-icons/fi";
import { buildApiCopyBlock, buildCurl, getApiHost } from "../data/munshotDataApis";
import { h2Style, introStyle, CopyableCodeBlock } from "./dashboardSkillGuideCommon";

const cardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "24px",
  backgroundColor: "#fafbff",
};

const pathStyle = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "14px",
  color: "#1e293b",
  wordBreak: "break-all",
  marginBottom: "8px",
};

const metaStyle = {
  fontSize: "13px",
  color: "#64748b",
  marginBottom: "12px",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#3730a3",
  marginBottom: "6px",
  marginTop: "12px",
};

const fieldListStyle = {
  color: "#4b5563",
  fontSize: "13px",
  lineHeight: 1.7,
  paddingLeft: "20px",
  marginBottom: "8px",
};

const primaryCopyStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  height: "40px",
  padding: "0 14px",
  borderRadius: "6px",
  border: "1px solid #3730a3",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

export const methodBadgeStyle = (method) => ({
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#ffffff",
  backgroundColor: method === "GET" ? "#059669" : "#2563eb",
  marginRight: "10px",
  verticalAlign: "middle",
});

export const useCopy = () => {
  const [copiedKey, setCopiedKey] = useState("");

  const copyText = useCallback(async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 2000);
  }, []);

  return { copiedKey, copyText };
};

const MunshotApiCard = ({ api, copiedKey, copyText }) => {
  const copyKey = `api-${api.id}`;
  const host = getApiHost(api);

  return (
    <article style={cardStyle} id={api.id}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <h3 style={{ ...h2Style, marginBottom: 0, flex: "1 1 220px" }}>
          <span style={methodBadgeStyle(api.method)}>{api.method}</span>
          {api.name}
        </h3>
        <button
          type="button"
          style={primaryCopyStyle}
          onClick={() => copyText(copyKey, buildApiCopyBlock(api))}
          aria-label={`Copy ${api.name}`}
        >
          {copiedKey === copyKey ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
          {copiedKey === copyKey ? "Copied" : "Copy this endpoint"}
        </button>
      </div>

      <p style={metaStyle}>
        <code>{api.id}</code> · service: <code>{api.service}</code> · rate limit: {api.rateLimit}
        {api.cacheTtl > 0 ? ` · cache: ${api.cacheTtl}s` : ""}
      </p>

      <p style={pathStyle}>
        {host}
        {api.path}
      </p>

      <p style={{ ...introStyle, marginBottom: "8px" }}>{api.shortDescription}</p>

      {api.requestFields?.length > 0 && (
        <>
          <div style={labelStyle}>Request fields</div>
          <ul style={fieldListStyle}>
            {api.requestFields.map((f) => (
              <li key={f.name}>
                <code>{f.name}</code> ({f.type}
                {f.required ? ", required" : ""}) — {f.description}
                {f.example ? (
                  <>
                    {" "}
                    · example: <code>{f.example}</code>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}

      {api.bodyExample && (
        <>
          <div style={labelStyle}>Request body example</div>
          <CopyableCodeBlock text={JSON.stringify(api.bodyExample, null, 2)} />
        </>
      )}

      {api.responseExample && (
        <>
          <div style={labelStyle}>Response example</div>
          <CopyableCodeBlock text={JSON.stringify(api.responseExample, null, 2)} />
        </>
      )}

      {api.responseNote && (
        <p style={{ ...introStyle, marginBottom: "8px", fontSize: "14px", color: "#64748b" }}>
          <strong>Response:</strong> {api.responseNote}
        </p>
      )}

      <div style={labelStyle}>cURL</div>
      <CopyableCodeBlock text={buildCurl(api)} />
    </article>
  );
};

export default MunshotApiCard;
